/**
 * Seller discount links: `/products/<slug>?deal=CODE`.
 *
 * A vendor sends the link on WhatsApp; the buyer taps it, and the code has
 * to survive the trip through the cart to checkout. It is kept on the
 * buyer's own device only — the coupon itself is validated server-side at
 * checkout, so a tampered value can never grant a discount.
 */

const KEY = "jb.deal";

export type StoredDeal = {
  code: string;
  /** Epoch ms after which we stop offering it. */
  exp: number;
  productId?: string | null;
};

const CODE_RE = /^[A-Z0-9_-]{3,32}$/;

/** Accept only well-formed codes so nothing odd reaches the coupon field. */
export function normalizeDealCode(raw: string | null | undefined): string | null {
  const c = String(raw ?? "")
    .trim()
    .toUpperCase();
  return CODE_RE.test(c) ? c : null;
}

export function readDeal(now = Date.now()): StoredDeal | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return null;
    const j = JSON.parse(raw) as StoredDeal;
    if (!j || typeof j.code !== "string" || typeof j.exp !== "number") return null;
    if (j.exp <= now) {
      window.localStorage.removeItem(KEY);
      return null;
    }
    return normalizeDealCode(j.code) ? j : null;
  } catch {
    return null;
  }
}

export function writeDeal(d: StoredDeal | null) {
  if (typeof window === "undefined") return;
  try {
    if (d) window.localStorage.setItem(KEY, JSON.stringify(d));
    else window.localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

/** Seller links last 72 hours; mirror that on the device. */
export const DEAL_TTL_MS = 72 * 60 * 60 * 1000;
